import { MAIN_FIELD_TYPE } from '../domain/schemas';
import type { GraphData, QueryParameter } from '../domain/schemas';

import {
  resolveGraphSlotChartHeight,
  resolveGraphSlotContainerHeight,
} from './graphSlotLayout';

/** 推移グラフ（店舗比較）で同時表示できるグラフの最大数 */
export const DATA_KI_GRAPH_MAX_SLOTS = 5;

/** 機種集計の複数推移グラフで recharts syncId に渡す固定値 */
export const DATA_KI_GRAPH_SYNC_ID = 'dataKiGraph';

/** DK-SIS 条件のデフォルト（表示なし） */
export const DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION = 'noGraph';

/** 1本表示時のグラフ高さ（px）。既存の単一表示と同等 */
export const DATA_KI_GRAPH_CHART_HEIGHT = 360;

/** 2本表示時の1本あたりのグラフ高さ（px）。日付ラベルが欠けない下限を確保 */
export const DATA_KI_GRAPH_COMPACT_CHART_HEIGHT = 240;

/** 3本表示時の1本あたりのグラフ高さ（px）。3本が Full HD に収まる下限 */
export const DATA_KI_GRAPH_DENSE_CHART_HEIGHT = 145;

/** 4本以上表示時の1本あたりのグラフ高さ（px） */
export const DATA_KI_GRAPH_OVERFLOW_CHART_HEIGHT = 140;

/**
 * 表示本数（未選択枠を含むスロット数）に応じたグラフ高さ（px）を返す。
 * 店舗比較は X 軸の日付ラベルが多いため、機種別推移より下限を厚めに取る。
 */
export const resolveDataKiGraphChartHeight = (graphCount: number): number =>
  resolveGraphSlotChartHeight(graphCount, {
    single: DATA_KI_GRAPH_CHART_HEIGHT,
    double: DATA_KI_GRAPH_COMPACT_CHART_HEIGHT,
    triple: DATA_KI_GRAPH_DENSE_CHART_HEIGHT,
    overflow: DATA_KI_GRAPH_OVERFLOW_CHART_HEIGHT,
  });

/** タイトル・共通条件・枠線など、グラフ本体以外の高さ（px） */
const DATA_KI_GRAPH_CONTAINER_CHROME_HEIGHT = 48;
const DATA_KI_GRAPH_CONTAINER_TITLE_HEIGHT = 48;
const DATA_KI_GRAPH_CONTAINER_CONDITION_HEIGHT = 112;
const DATA_KI_GRAPH_CONTAINER_SHARED_LEGEND_HEIGHT = 40;
const DATA_KI_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT = 56;
const DATA_KI_GRAPH_CONTAINER_SLOT_GAP = 16;
const DATA_KI_GRAPH_CONTAINER_FOOTER_HEIGHT = 52;

/**
 * 複数スロット表示時に ResizableWrapper が収めるべき高さ（px）を返す。
 * 機種別推移と同様、グラフ本数に応じて領域を広げる。
 */
export const resolveDataKiGraphContainerHeight = (
  slotCount: number,
  options?: { hasMultipleGraphs?: boolean }
): number =>
  resolveGraphSlotContainerHeight(
    slotCount,
    {
      chromeHeight: DATA_KI_GRAPH_CONTAINER_CHROME_HEIGHT,
      titleHeight: DATA_KI_GRAPH_CONTAINER_TITLE_HEIGHT,
      controlsHeight: DATA_KI_GRAPH_CONTAINER_CONDITION_HEIGHT,
      slotControlsHeight: DATA_KI_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT,
      slotGap: DATA_KI_GRAPH_CONTAINER_SLOT_GAP,
      footerHeight: DATA_KI_GRAPH_CONTAINER_FOOTER_HEIGHT,
      sharedLegendHeight: DATA_KI_GRAPH_CONTAINER_SHARED_LEGEND_HEIGHT,
      resolveChartHeight: resolveDataKiGraphChartHeight,
    },
    options
  );

/** 1本のグラフ = 表示項目 + DK-SIS 条件 */
export type DataKiGraphSlotConfig = {
  field: GraphFieldType | '';
  dkSisCondition: string;
};

export type DataKiGraphFieldItem = {
  field: GraphFieldType;
  dkSisCondition: string;
  data: GraphData;
};

export type DataKiGraphParams = {
  /**
   * グラフ API のクエリ param `field`（1項目分）。
   * 複数グラフ表示以前のお気に入りはこちらが保存されている。
   */
  field?: GraphFieldType;
  /**
   * 画面に出すグラフスロット一覧（最大 DATA_KI_GRAPH_MAX_SLOTS 件）。
   * API リクエストでは Saga がスロットごとに field と DK-SIS 条件へ分けて送る。
   */
  graphSlots?: DataKiGraphSlotConfig[];
  seriesType?: string;
  containsAverage?: boolean;
  dateType?: GraphDateType;
  makers?: string[];
  makerGroups?: string[];
  kiList?: string[];
  sisTypes?: string[];
  shuList?: string[];
  hallsForDonyuJokyo?: string[];
  isDonyuForDonyuJokyo?: boolean;
  halls?: string[];
  areas?: string[];
  startDate?: string | null;
  endDate?: string | null;
  excludeToday?: boolean;
  startComparisonDate?: string | null;
  endComparisonDate?: string | null;
  shuGroupIds?: string[];
  sisBreakEvenForPachinko?: string;
  sisBreakEvenForPachislo?: string;
  sisRank?: string;
  sisArea?: string;
  needsSis?: boolean;
  makersForSijiritu?: string[];
  kiListForSijiritu?: string[];
  sisTypesForSijiritu?: string[];
  tagIds?: string[];
  /**
   * true かつ tagIds 指定時、指定 tagIds のいずれのタグも付いていない機種のみで集計する
   */
  isExcludeForTagIds?: boolean;
};

export const GRAPH_FIELD_TYPE = {
  DAYS: 'days',
  SO_DAISU: 'soDaisu',
  SO_DAISU_COMPARISON: 'soDaisuComparison',
  HEIKIN_DAISU: 'heikinDaisu',
  GEN_SETSU: 'genSetsu',
  DONYU_HALL_SU: 'donyuHallSu',
  KEIKA_WEEK: 'keikaWeek',
  OUT: 'out',
  OUT_COMPARISON: 'outComparison',
  OUT_SIJI_RITU: 'outSijiRitu',
  OUT_SIJI_RITU_COMPARISON: 'outSijiRituComparison',
  KADO_JIKAN: 'kadoJikan',
  DAI_YUGI_JIKAN: 'daiYugiJikan',
  DAI_YUGI_JIKAN_COMPARISON: 'daiYugiJikanComparison',
  JIKAN_SIJI_RITU: 'jikanSijiRitu',
  JIKAN_OUT: 'jikanOut',
  SAFE: 'safe',
  DIFF: 'diff',
  DTMART: 'dtmart',
  TKSHO: 'tksho',
  KYAKUTAIRT: 'kyakutairt',
  BO_AVG: 'boAvg',
  MAX_MY: 'maxMy',
  DAI_URIAGE: 'daiUriage',
  DAI_URIAGE_SIJI_RITU: 'daiUriageSijiRitu',
  DAI_ARARI: 'daiArari',
  DAI_ARARI_COMPARISON: 'daiArariComparison',
  DAI_ARARI_SIJI_RITU: 'daiArariSijiRitu',
  DAI_ARARI_SIJI_RITU_COMPARISON: 'daiArariSijiRituComparison',
  TMA_TANKA: 'tmaTanka',
  TMA_ARARI: 'tmaArari',
  TMA_ARARI_COMPARISON: 'tmaArariComparison',
  JIKAN_URIAGE: 'jikanUriage',
  JIKAN_ARARI: 'jikanArari',
  YUGI_JIKAN_URIAGE: 'yugiJikanUriage',
  YUGI_JIKAN_ARARI: 'yugiJikanArari',
  RIEKI_RITU: 'riekiRitu',
  RIEKI_RITU_COMPARISON: 'riekiRituComparison',
  KIKAI_WARISU: 'kikaiWarisu',
  GENKIN_URIAGE_GK: 'genkinUriageGk',
  URIAGE_GK: 'uriageGk',
  ARARI_GK: 'arariGk',
  REPLAY_GAK: 'replayGak',
  REPLAY_RITU: 'replayRitu',
  BASE_CNT: 'baseCnt',
  YUKO_S: 'yukoS',
  YUKO_S_COMPARISON: 'yukoSComparison',
  NYUSHO_S: 'nyushoS',
  NYUSHO_S_COMPARISON: 'nyushoSComparison',
  S1: 's1',
  S2: 's2',
  X1000_YEN_START: 'x1000YenStart',
  X1000_YEN_START_COMPARISON: 'x1000YenStartComparison',
  S_YUKO_RITU: 'sYukoRitu',
  BY_CNT: 'byCnt',
  BYMIN: 'bymin',
  BYMIN_COMPARISON: 'byminComparison',
  BA: 'ba',
  SA: 'sa',
  T1_O: 't1O',
  T1_Y: 't1Y',
  HSI_T1_Y: 'hsiT1Y',
  TO_CNT: 'toCnt',
  TY: 'ty',
  TS: 'ts',
  BB_SU: 'bbSu',
  RB_SU: 'rbSu',
  BB_KAKURT: 'bbKakurt',
  RB_KAKURT: 'rbKakurt',
  GSE_KAKURT: 'gseKakurt',
} as const;
export type GraphFieldType =
  (typeof GRAPH_FIELD_TYPE)[keyof typeof GRAPH_FIELD_TYPE];

const GRAPH_FIELD_TYPE_VALUE_SET = new Set<string>(
  Object.values(GRAPH_FIELD_TYPE)
);

/**
 * 機種集計の推移グラフ（店舗比較）で使う API の field が GraphFieldType として安全か判定する。
 */
export function isDataKiGraphFieldType(value: string): value is GraphFieldType {
  return GRAPH_FIELD_TYPE_VALUE_SET.has(value);
}

export const GRAPH_DATE_TYPE = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
} as const;
export type GraphDateType =
  (typeof GRAPH_DATE_TYPE)[keyof typeof GRAPH_DATE_TYPE];

const GRAPH_DATE_TYPE_VALUE_SET = new Set<string>(
  Object.values(GRAPH_DATE_TYPE)
);

/**
 * 推移グラフの期間種別（日次／週次／月次）として API と整合する値か判定する。
 */
export function isGraphDateType(value: string): value is GraphDateType {
  return GRAPH_DATE_TYPE_VALUE_SET.has(value);
}

/** メーカー/メーカーグループ配下でも、機種行を開いたグラフでは DK-SIS 条件を表示する。 */
export const shouldShowDkSisFieldsSelector = (
  mainField: string | undefined,
  graphOpenedQueryParameter?: QueryParameter
): boolean => {
  if (
    mainField !== MAIN_FIELD_TYPE.MAKER &&
    mainField !== MAIN_FIELD_TYPE.MAKER_GROUP
  ) {
    return mainField !== undefined;
  }
  return graphOpenedQueryParameter?.name === 'kiList';
};

const isDkSisUnsupportedGraphOpenedValue = (
  openedValue: string | null | undefined
): boolean =>
  openedValue === 'kiTagAverage' ||
  openedValue === 'excludedKiTagAverage' ||
  openedValue?.startsWith('onlyOneKiTagAverage_') === true;

/**
 * タグ平均系の行では DK-SIS を無効化する。
 * graphOpenedQueryParameter がある場合は、旧 GraphConditionSelector の selectedMainField 判定と同じく
 * 「実際に開いた行」を優先する。
 */
export const isDkSisDisabledForKiGraphRow = (params: {
  graphOpenedQueryParameter?: QueryParameter;
  isKiTagAverageGraphDisplay: boolean;
  isOnlyOneKiTagAverageGraphDisplay: boolean;
  isExcludedKiTagAverageGraphDisplay: boolean;
}): boolean => {
  const openedValue = params.graphOpenedQueryParameter?.value;
  if (openedValue !== undefined) {
    return isDkSisUnsupportedGraphOpenedValue(openedValue);
  }
  return (
    params.isKiTagAverageGraphDisplay ||
    params.isOnlyOneKiTagAverageGraphDisplay ||
    params.isExcludedKiTagAverageGraphDisplay
  );
};

/* eslint-disable no-unused-vars */
export type DataKiGraph = {
  setting: DataKiGraphParams;
  data: GraphData;
};

export type DataKiGraphCollection = {
  setting: DataKiGraphParams;
  graphList: DataKiGraphFieldItem[];
};
/* eslint-enable no-unused-vars */

/**
 * 選択店舗に応じてグラフデータの列を絞り込む。
 * DK-SIS 列は選択店舗に関わらず表示対象に残す。
 */
export const filterGraphDataBySelectHalls = (
  data: GraphData,
  selectHalls: string[]
): GraphData => {
  const removeIdx = data.columns
    .reduce((prev: number[], current, idx) => {
      // 選択店舗以外は非表示。ただし DK-SIS 列は選択店舗に関わらず常に残す
      if (
        !selectHalls.includes(current.name) &&
        !current.name.toLowerCase().includes('dk-sis')
      ) {
        return [...prev, idx];
      }
      return prev;
    }, [])
    // 平均・合計行（列 index=1）は「平均」選択時のみ表示対象に残す
    .filter((item) =>
      selectHalls.includes('null') && item === 1 ? false : true
    );

  const columns = data.columns.filter((_item, idx) => {
    if (idx === 0) {
      return true; // 日付列（index=0）は必ず含む
    }
    return !removeIdx.includes(idx);
  });
  const rows = data.rows.map((row) =>
    row.filter((_item, idx) => {
      if (idx === 0) {
        return true; // 日付列（index=0）は必ず含む
      }
      return !removeIdx.includes(idx);
    })
  );

  return { columns, rows };
};
